import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {DataTable} from "components/dataTable/index.jsx";
import {useState, useMemo, useCallback, useEffect} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

export default observer(function AdminSales(props) {
  const appDataStore = useAppDataStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showNoveltyModal, setShowNoveltyModal] = useState(false);
  const [novelty, setNovelty] = useState(null);
  const [loadingNovelty, setLoadingNovelty] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [noveltyForm, setNoveltyForm] = useState({status: '', solution: '', rejectionReason: '', applyRefund: false});
  const [savingNovelty, setSavingNovelty] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({type: '', description: '', evidence: null});
  const [reportingNovelty, setReportingNovelty] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendSale, setResendSale] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [sendingResend, setSendingResend] = useState(false);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({subjectIds: '', type: '', description: '', status: 'approved', applyRefund: false});
  const [bulkStep, setBulkStep] = useState('form'); // 'form' | 'preview' | 'result'
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submittingBulk, setSubmittingBulk] = useState(false);

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  const StatusBadge = ({status}) => {
    const statusColors = {
      success: "bg-green-100 text-green-800",
      error: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
      SUCCESS: "bg-green-100 text-green-800",
      ERROR: "bg-red-100 text-red-800",
      PENDING: "bg-yellow-100 text-yellow-800"
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[status] || "bg-gray-100 text-gray-800"}`}>
        {status}
      </span>
    );
  };

  const handleViewNovelty = useCallback(async (sale) => {
    setSelectedSale(sale);
    setLoadingNovelty(true);
    setEditMode(false);
    setNovelty(null);
    setShowNoveltyModal(true);
    try {
      const response = await appDataStore.post("/sales/get-novelty", {saleId: sale.id});
      setNovelty(response.novelty);
      setNoveltyForm({
        status: response.novelty.status,
        solution: response.novelty.solution || '',
        rejectionReason: response.novelty.rejectionReason || '',
        applyRefund: false
      });
    } catch (err) {
      console.error("Error al obtener novedad:", err);
      alert("Error al obtener la novedad: " + err.message);
      setShowNoveltyModal(false);
    } finally {
      setLoadingNovelty(false);
    }
  }, [appDataStore]);

  const handleSaveNovelty = async () => {
    try {
      setSavingNovelty(true);
      await appDataStore.post("/sales/update-novelty", {
        noveltyId: novelty.id,
        status: noveltyForm.status,
        solution: noveltyForm.solution || null,
        rejectionReason: noveltyForm.rejectionReason || null,
        applyRefund: noveltyForm.applyRefund
      });
      setNovelty(prev => ({...prev, status: noveltyForm.status, solution: noveltyForm.solution, rejectionReason: noveltyForm.rejectionReason}));
      setEditMode(false);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al actualizar novedad:", err);
      alert("Error al actualizar: " + err.message);
    } finally {
      setSavingNovelty(false);
    }
  };

  const handleResendEmail = useCallback((sale) => {
    setResendSale(sale);
    setResendEmail(sale.email || '');
    setShowResendModal(true);
  }, []);

  const submitResend = async () => {
    try {
      setSendingResend(true);
      await appDataStore.post("/sales/resend-email", {saleId: resendSale.id, alternativeMail: resendEmail || undefined});
      setShowResendModal(false);
      setResendSale(null);
      setResendEmail('');
    } catch (err) {
      alert("Error al reenviar: " + err.message);
    } finally {
      setSendingResend(false);
    }
  };

  const handleReportNovelty = useCallback((sale) => {
    setSelectedSale(sale);
    setReportForm({type: '', description: '', evidence: null});
    setShowReportModal(true);
  }, []);

  const handleEvidenceChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setReportForm(prev => ({...prev, evidence: event.target.result}));
    reader.readAsDataURL(file);
  };

  const submitReport = async () => {
    if (!reportForm.type || !reportForm.description.trim()) {
      alert('Por favor completa todos los campos');
      return;
    }
    try {
      setReportingNovelty(true);
      await appDataStore.post("/sales/report-novelty", {
        saleId: selectedSale.id,
        noveltyType: reportForm.type,
        description: reportForm.description.trim(),
        evidence: reportForm.evidence
      });
      setShowReportModal(false);
      setSelectedSale(null);
      setReportForm({type: '', description: '', evidence: null});
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al reportar novedad:", err);
      alert("Error al reportar novedad: " + err.message);
    } finally {
      setReportingNovelty(false);
    }
  };

  const fetchBulkPreview = async () => {
    if (!bulkForm.subjectIds.trim() || !bulkForm.type || !bulkForm.description.trim()) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }
    try {
      setLoadingPreview(true);
      const response = await appDataStore.post("/sales/preview-bulk-novelty", {
        subjectIds: bulkForm.subjectIds
      });
      setBulkPreview(response);
      setBulkStep('preview');
    } catch (err) {
      console.error("Error al generar preview:", err);
      alert("Error: " + err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const submitBulkNovelty = async () => {
    try {
      setSubmittingBulk(true);
      const response = await appDataStore.post("/sales/bulk-novelty", {
        subjectIds: bulkForm.subjectIds,
        noveltyType: bulkForm.type,
        description: bulkForm.description.trim(),
        status: bulkForm.status,
        applyRefund: bulkForm.applyRefund
      });
      setBulkResult(response);
      setBulkStep('result');
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error en novedades masivas:", err);
      alert("Error: " + err.message);
    } finally {
      setSubmittingBulk(false);
    }
  };

  const noveltyStatusLabel = (status) => {
    if (status === 'approved') return 'Aprobada';
    if (status === 'rejected') return 'Rechazada';
    return 'Pendiente';
  };

  const noveltyStatusClass = (status) => {
    if (status === 'approved') return 'text-green-700';
    if (status === 'rejected') return 'text-red-700';
    return 'text-yellow-700';
  };

  const NoveltyFilter = ({column}) => {
    const [value, setValue] = useState('');
    useEffect(() => {
      if (value === '1') column.setFilterValue([{key: 'novelty', operator: 'eq', value: 1}]);
      else if (value === '0') column.setFilterValue([{key: 'novelty', operator: 'eq', value: 0}]);
      else column.setFilterValue([]);
    }, [value]);
    return (
      <select
        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="">Todas</option>
        <option value="1">Con novedad</option>
        <option value="0">Sin novedad</option>
      </select>
    );
  };

  const columns = useMemo(() => ([
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({cell}) => {
        return cell.getValue();
      }
    },
    {
      accessorKey: 'User',
      header: 'Distribuidor',
      cell: ({cell}) => {
        const user = cell.getValue();
        return user ? `${user.name} (${user.email})` : '-';
      },
      enableColumnFilter: false,
      exportFormat: (row) => row.User ? `${row.User.name} (${row.User.email})` : '-'
    },
    {
      accessorKey: 'subjectId',
      header: 'Subject ID',
      opFilter: 'eq',
    },
    {
      accessorKey: 'product',
      header: 'Producto',
      opFilter: 'eq',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'customerName',
      header: 'Cliente',
    },
    {
      accessorKey: 'reference',
      header: 'Referencia',
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.amount
    },
    {
      accessorKey: 'result',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => <StatusBadge status={cell.getValue()}/>
    },
    {
      accessorKey: 'transactionId',
      header: 'Transaction ID',
    },
    {
      accessorKey: 'novelty',
      header: 'Novedad',
      cell: ({row, cell}) => {
        if (!cell.getValue()) {
          return (
            <button
              onClick={() => handleReportNovelty(row.original)}
              className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200 border border-gray-300"
            >
              Reportar
            </button>
          );
        }
        const status = row.original.SalesNovelty?.status;
        if (status === 'approved') {
          return (
            <button
              onClick={() => handleViewNovelty(row.original)}
              className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium hover:bg-green-200 border border-green-300"
            >
              ✓ Aprobada
            </button>
          );
        }
        if (status === 'rejected') {
          return (
            <button
              onClick={() => handleViewNovelty(row.original)}
              className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200 border border-red-300"
            >
              ✗ Rechazada
            </button>
          );
        }
        return (
          <button
            onClick={() => handleViewNovelty(row.original)}
            className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium hover:bg-orange-200 border border-orange-300"
          >
            ⚠ Pendiente
          </button>
        );
      },
      exportFormat: (row) => row.novelty ? 'Sí' : 'No',
      customFilter: (column) => <NoveltyFilter column={column}/>
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({cell}) => moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss'),
      exportFormat: (row) => moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      id: 'actions',
      header: 'Acciones',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({row}) => {
        const sale = row.original;
        if (!sale.activationUrl && sale.result !== 'SUCCESS') return null;
        return (
          <button
            onClick={() => handleResendEmail(sale)}
            className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 border border-blue-300"
          >
            Reenviar email
          </button>
        );
      }
    }
  ]), [handleViewNovelty, handleReportNovelty, handleResendEmail]);

  const dataFetch = useCallback(async (
    records,
    currentPage,
    order,
    search,
    filter
  ) => {
    try {
      const requestData = {
        records,
        currentPage,
        order: [['id', 'DESC']],
        search,
        filter,
        attributes: ['id', 'userId', 'amount', 'balanceBefore', 'balanceAfter', 'result', 'error', 'transactionId', 'reference', 'phoneNumber', 'email', 'customerName', 'novelty', 'product', 'createdAt', 'subjectId', 'activationUrl'],
        searchAttributes: ['phoneNumber', 'email', 'customerName', 'reference', 'transactionId']
      };

      const response = await appDataStore.post("/sales/list-all", requestData);

      return {
        data: response.data || [],
        totalRows: response.total || 0,
        totalPages: response.totalPages || 1
      };
    } catch (err) {
      console.error("Error al cargar ventas:", err);
      return {
        data: [],
        totalRows: 0,
        totalPages: 1
      };
    }
  }, [appDataStore]);

  if (!hasMasterScope) {
    return (
      <div className="p-2 max-w-7xl mx-auto text-black">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Historial de Ventas</h2>
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
              <p className="text-red-700">
                No tienes permisos para acceder al historial completo de ventas. Se requiere el
                scope <strong>Master</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-2 mx-auto text-black">
        <div className="flex justify-end mb-2">
          <button
            onClick={() => { setBulkForm({subjectIds: '', type: '', description: '', status: 'approved', applyRefund: false}); setBulkPreview(null); setBulkResult(null); setBulkStep('form'); setShowBulkModal(true); }}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm font-medium"
          >
            Novedades Masivas
          </button>
        </div>
        <DataTable
          key={refreshKey}
          columns={columns}
          dataFetch={dataFetch}
          enablePagination
          enableSearch
          enableColumnFilter
          enableExport
          exportFileName="historial-ventas"
        />
      </div>

      {/* Modal Ver/Gestionar Novedad */}
      {showNoveltyModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Novedad Reportada</h3>

            {/* Info de la venta */}
            <div className="mb-4 p-3 bg-gray-100 rounded text-sm text-gray-600 space-y-1">
              <p><strong>ID:</strong> {selectedSale.id}</p>
              <p><strong>Transaction ID:</strong> {selectedSale.transactionId}</p>
              <p><strong>Cliente:</strong> {selectedSale.customerName}</p>
              <p><strong>Referencia:</strong> {selectedSale.reference}</p>
              <p><strong>Monto:</strong> {formatters.cash(selectedSale.amount)}</p>
            </div>

            {loadingNovelty && (
              <div className="text-center py-8 text-gray-500">Cargando novedad...</div>
            )}

            {!loadingNovelty && novelty && (
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs text-gray-500 mb-1">Tipo de Novedad</p>
                  <p className="text-sm font-medium text-gray-800">{novelty.noveltyType}</p>
                </div>

                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs text-gray-500 mb-1">Descripción</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{novelty.description}</p>
                </div>

                {novelty.evidence && (
                  <div className="p-3 bg-blue-50 rounded border border-blue-200">
                    <p className="text-xs text-gray-500 mb-1">Evidencia</p>
                    <a href={novelty.evidence} target="_blank" rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm">
                      Ver archivo adjunto
                    </a>
                  </div>
                )}

                {/* Estado - editable en modo edición */}
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Estado</p>
                  {editMode ? (
                    <select
                      value={noveltyForm.status}
                      onChange={(e) => setNoveltyForm(prev => ({...prev, status: e.target.value}))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="pending">Pendiente</option>
                      <option value="approved">Aprobada</option>
                      <option value="rejected">Rechazada</option>
                    </select>
                  ) : (
                    <p className={`text-sm font-semibold ${noveltyStatusClass(novelty.status)}`}>
                      {noveltyStatusLabel(novelty.status)}
                    </p>
                  )}
                </div>

                {/* Reembolso - solo visible al aprobar */}
                {editMode && noveltyForm.status === 'approved' && (
                  <div className="p-3 bg-green-50 rounded border border-green-300">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={noveltyForm.applyRefund}
                        onChange={(e) => setNoveltyForm(prev => ({...prev, applyRefund: e.target.checked}))}
                        className="w-4 h-4 accent-green-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-green-800">Generar reembolso al saldo</p>
                        <p className="text-xs text-green-600">
                          Se abonará {formatters.cash(selectedSale?.amount)} al saldo prepago del distribuidor
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Solución */}
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Solución Aplicada</p>
                  {editMode ? (
                    <textarea
                      value={noveltyForm.solution}
                      onChange={(e) => setNoveltyForm(prev => ({...prev, solution: e.target.value}))}
                      placeholder="Describe la solución aplicada..."
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm h-20 resize-none"
                    />
                  ) : (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                      {novelty.solution || <span className="text-gray-400 italic">Sin solución registrada</span>}
                    </p>
                  )}
                </div>

                {/* Motivo de rechazo */}
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Motivo de Rechazo</p>
                  {editMode ? (
                    <textarea
                      value={noveltyForm.rejectionReason}
                      onChange={(e) => setNoveltyForm(prev => ({...prev, rejectionReason: e.target.value}))}
                      placeholder="Indica el motivo de rechazo si aplica..."
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm h-20 resize-none"
                    />
                  ) : (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                      {novelty.rejectionReason || <span className="text-gray-400 italic">Sin motivo registrado</span>}
                    </p>
                  )}
                </div>

                <div className="text-xs text-gray-400 space-y-0.5">
                  <p>Reportado: {moment(novelty.createdAt).format('YYYY-MM-DD HH:mm:ss')}</p>
                  {novelty.updatedAt && (
                    <p>Actualizado: {moment(novelty.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</p>
                  )}
                </div>

                {/* Botones de acción */}
                <div className="flex gap-2 pt-2">
                  {editMode ? (
                    <>
                      <button
                        onClick={handleSaveNovelty}
                        disabled={savingNovelty}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 text-sm font-medium"
                      >
                        {savingNovelty ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button
                        onClick={() => {
                          setEditMode(false);
                          setNoveltyForm({
                            status: novelty.status,
                            solution: novelty.solution || '',
                            rejectionReason: novelty.rejectionReason || ''
                          });
                        }}
                        disabled={savingNovelty}
                        className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 text-sm font-medium"
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      {novelty.status === 'pending' && (
                        <button
                          onClick={() => setEditMode(true)}
                          className="flex-1 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm font-medium"
                        >
                          Gestionar
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowNoveltyModal(false);
                          setSelectedSale(null);
                          setNovelty(null);
                        }}
                        className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 text-sm font-medium"
                      >
                        Cerrar
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Novedades Masivas */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-[95vw] max-h-[90vh] overflow-y-auto">

            {/* Header con paso actual */}
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Novedades Masivas</h3>
              <div className="flex items-center gap-1 text-xs text-gray-400 ml-auto">
                <span className={`px-2 py-0.5 rounded-full font-medium ${bulkStep === 'form' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400'}`}>1. Datos</span>
                <span className="text-gray-300">›</span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${bulkStep === 'preview' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400'}`}>2. Preview</span>
                <span className="text-gray-300">›</span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${bulkStep === 'result' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400'}`}>3. Resultado</span>
              </div>
            </div>

            {/* Paso 1: Formulario */}
            {bulkStep === 'form' && (
              <div className="space-y-4">
                <p className="text-xs text-gray-500">Completa los datos y revisa el preview antes de aplicar.</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject IDs <span className="text-red-500">*</span></label>
                  <textarea
                    value={bulkForm.subjectIds}
                    onChange={(e) => setBulkForm(prev => ({...prev, subjectIds: e.target.value}))}
                    placeholder={"Escribe un Subject ID por línea:\nABC123\nDEF456\nGHI789"}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm h-28 resize-none font-mono"
                  />
                  <p className="text-xs text-gray-400 mt-1">Un Subject ID por línea</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Novedad <span className="text-red-500">*</span></label>
                  <select
                    value={bulkForm.type}
                    onChange={(e) => setBulkForm(prev => ({...prev, type: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="">Selecciona un tipo</option>
                    <option value="error_monto">Error en Monto</option>
                    <option value="error_saldo">Error en Saldo</option>
                    <option value="cliente_reclamo">Reclamo del Cliente</option>
                    <option value="fallo_procesamiento">Fallo en Procesamiento</option>
                    <option value="transaccion_duplicada">Transacción Duplicada</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción <span className="text-red-500">*</span></label>
                  <textarea
                    value={bulkForm.description}
                    onChange={(e) => setBulkForm(prev => ({...prev, description: e.target.value}))}
                    placeholder="Describe el problema o novedad..."
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm h-20 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resolución <span className="text-red-500">*</span></label>
                  <select
                    value={bulkForm.status}
                    onChange={(e) => setBulkForm(prev => ({...prev, status: e.target.value, applyRefund: false}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="approved">Favorable (Aprobada)</option>
                    <option value="rejected">No Favorable (Rechazada)</option>
                  </select>
                </div>
                {bulkForm.status === 'approved' && (
                  <div className="p-3 bg-green-50 rounded border border-green-300">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkForm.applyRefund}
                        onChange={(e) => setBulkForm(prev => ({...prev, applyRefund: e.target.checked}))}
                        className="w-4 h-4 accent-green-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-green-800">Reembolsar saldo</p>
                        <p className="text-xs text-green-600">Se abonará el monto de cada venta al saldo prepago del distribuidor</p>
                      </div>
                    </label>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={fetchBulkPreview}
                    disabled={loadingPreview || !bulkForm.subjectIds.trim() || !bulkForm.type || !bulkForm.description.trim()}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 text-sm font-medium"
                  >
                    {loadingPreview ? 'Consultando...' : 'Ver Preview →'}
                  </button>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    disabled={loadingPreview}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:bg-gray-400 text-sm font-medium"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Paso 2: Preview */}
            {bulkStep === 'preview' && bulkPreview && (
              <div className="space-y-4">
                {/* Resumen config */}
                <div className="p-3 bg-purple-50 border border-purple-200 rounded text-xs text-purple-800 grid grid-cols-2 gap-x-4 gap-y-1">
                  <span><strong>Tipo:</strong> {bulkForm.type}</span>
                  <span><strong>Resolución:</strong> {bulkForm.status === 'approved' ? 'Favorable' : bulkForm.status === 'rejected' ? 'No Favorable' : 'Pendiente'}</span>
                  {bulkForm.applyRefund && <span className="col-span-2 text-green-700 font-medium">Con reembolso de saldo</span>}
                  <span className="col-span-2"><strong>Descripción:</strong> {bulkForm.description}</span>
                </div>

                {/* IDs no encontrados */}
                {bulkPreview.notFound?.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-xs font-semibold text-red-700 mb-1">No encontrados ({bulkPreview.notFound.length})</p>
                    <p className="text-xs text-red-600 font-mono break-all">{bulkPreview.notFound.join(', ')}</p>
                  </div>
                )}

                {/* Tabla de ventas encontradas */}
                {bulkPreview.found?.length > 0 ? (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">Ventas que se van a afectar ({bulkPreview.found.length})</p>
                    <div className="overflow-x-auto rounded border border-gray-200">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-gray-600 font-medium">Subject ID</th>
                            <th className="px-3 py-2 text-left text-gray-600 font-medium">Cliente</th>
                            <th className="px-3 py-2 text-left text-gray-600 font-medium">Distribuidor</th>
                            <th className="px-3 py-2 text-right text-gray-600 font-medium">Monto</th>
                            <th className="px-3 py-2 text-center text-gray-600 font-medium">Estado Nuplin</th>
                            <th className="px-3 py-2 text-center text-gray-600 font-medium">Estado plataforma</th>
                            <th className="px-3 py-2 text-center text-gray-600 font-medium">Novedad actual</th>
                            <th className="px-3 py-2 text-center text-gray-600 font-medium">Acción</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {bulkPreview.found.map(sale => {
                            const willRefund = bulkForm.applyRefund && bulkForm.status === 'approved' && sale.currentNoveltyStatus !== 'approved';
                            return (
                              <tr key={sale.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-mono text-gray-700">{sale.subjectId}</td>
                                <td className="px-3 py-2 text-gray-600">{sale.customerName || '—'}</td>
                                <td className="px-3 py-2 text-gray-600">{sale.distributor?.name || '—'}</td>
                                <td className="px-3 py-2 text-right font-medium text-gray-700">{formatters.cash(sale.amount)}</td>
                                <td className="px-3 py-2 text-center">
                                  {sale.nuplinState ? (
                                    <span className={`px-1.5 py-0.5 rounded font-medium font-mono ${
                                      sale.nuplinState?.includes('COMPLET') ? 'bg-green-100 text-green-700' :
                                      sale.nuplinState?.includes('CANCEL') ? 'bg-red-100 text-red-700' :
                                      sale.nuplinState?.includes('INITIAT') ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {sale.nuplinState}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {sale.tokenStatus ? (
                                    <span className={`px-1.5 py-0.5 rounded font-medium ${
                                      sale.tokenStatus === 'used'      ? 'bg-blue-100 text-blue-700' :
                                      sale.tokenStatus === 'available' ? 'bg-green-100 text-green-700' :
                                      sale.tokenStatus === 'expired'   ? 'bg-gray-100 text-gray-500' :
                                      sale.tokenStatus === 'error'     ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {sale.tokenStatus === 'used'      ? 'Usado' :
                                       sale.tokenStatus === 'available' ? 'Disponible' :
                                       sale.tokenStatus === 'expired'   ? 'Expirado' :
                                       sale.tokenStatus === 'error'     ? 'Error' :
                                       sale.tokenStatus}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {sale.hasNovelty ? (
                                    <span className={`px-1.5 py-0.5 rounded font-medium ${
                                      sale.currentNoveltyStatus === 'approved' ? 'bg-green-100 text-green-700' :
                                      sale.currentNoveltyStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                                      'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {sale.currentNoveltyStatus === 'approved' ? 'Aprobada' :
                                       sale.currentNoveltyStatus === 'rejected' ? 'Rechazada' : 'Pendiente'}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">Sin novedad</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`px-1.5 py-0.5 rounded font-medium ${sale.hasNovelty ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {sale.hasNovelty ? 'Actualizar' : 'Crear'}
                                  </span>
                                  {willRefund && (
                                    <span className="ml-1 px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">+ Reembolso</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {bulkForm.applyRefund && bulkForm.status === 'approved' && (
                      <p className="text-xs text-green-700 mt-2 font-medium">
                        Total a reembolsar: {formatters.cash(
                          bulkPreview.found
                            .filter(s => s.currentNoveltyStatus !== 'approved')
                            .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0)
                        )}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700 text-center">
                    No se encontró ninguna venta con los Subject IDs indicados.
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setBulkStep('form')}
                    disabled={submittingBulk}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:bg-gray-400 text-sm font-medium"
                  >
                    ← Volver
                  </button>
                  <button
                    onClick={submitBulkNovelty}
                    disabled={submittingBulk || !bulkPreview.found?.length}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 text-sm font-medium"
                  >
                    {submittingBulk ? 'Aplicando...' : `Confirmar y aplicar (${bulkPreview.found?.length ?? 0} ventas)`}
                  </button>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    disabled={submittingBulk}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:bg-gray-400 text-sm font-medium"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Paso 3: Resultado */}
            {bulkStep === 'result' && bulkResult && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded border border-gray-200 space-y-3">
                  <p className="font-semibold text-gray-700">Proceso completado</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-blue-50 rounded text-center">
                      <p className="text-xs text-gray-500">Procesadas</p>
                      <p className="text-xl font-bold text-blue-700">{bulkResult.results?.processed ?? 0}</p>
                    </div>
                    <div className="p-2 bg-green-50 rounded text-center">
                      <p className="text-xs text-gray-500">Creadas</p>
                      <p className="text-xl font-bold text-green-700">{bulkResult.results?.created ?? 0}</p>
                    </div>
                    <div className="p-2 bg-yellow-50 rounded text-center">
                      <p className="text-xs text-gray-500">Actualizadas</p>
                      <p className="text-xl font-bold text-yellow-700">{bulkResult.results?.updated ?? 0}</p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded text-center">
                      <p className="text-xs text-gray-500">Reembolsos</p>
                      <p className="text-xl font-bold text-purple-700">{bulkResult.results?.refunded ?? 0}</p>
                    </div>
                  </div>
                  {bulkResult.notFound?.length > 0 && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded">
                      <p className="text-xs font-medium text-red-700 mb-1">No encontrados ({bulkResult.notFound.length}):</p>
                      <p className="text-xs text-red-600 font-mono break-all">{bulkResult.notFound.join(', ')}</p>
                    </div>
                  )}
                  {bulkResult.results?.errors?.length > 0 && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded">
                      <p className="text-xs font-medium text-red-700 mb-1">Errores ({bulkResult.results.errors.length}):</p>
                      {bulkResult.results.errors.map((e, i) => (
                        <p key={i} className="text-xs text-red-600">Venta {e.saleId} ({e.subjectId}): {e.error}</p>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setBulkResult(null); setBulkPreview(null); setBulkForm({subjectIds: '', type: '', description: '', status: 'approved', applyRefund: false}); setBulkStep('form'); }}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm font-medium"
                  >
                    Nueva operación
                  </button>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 text-sm font-medium"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Modal Reenviar Email */}
      {showResendModal && resendSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Reenviar Email</h3>
            <div className="mb-4 p-3 bg-gray-100 rounded text-sm text-gray-600 space-y-1">
              <p><strong>ID:</strong> {resendSale.id}</p>
              <p><strong>Cliente:</strong> {resendSale.customerName}</p>
              <p><strong>Email original:</strong> {resendSale.email || '—'}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email destino</label>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Dejar vacío para usar el email original"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={submitResend}
                disabled={sendingResend}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 text-sm font-medium"
              >
                {sendingResend ? 'Enviando...' : 'Enviar'}
              </button>
              <button
                onClick={() => { setShowResendModal(false); setResendSale(null); setResendEmail(''); }}
                disabled={sendingResend}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 text-sm font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reportar Novedad */}
      {showReportModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Reportar Novedad en Venta</h3>

            <div className="mb-4 p-3 bg-gray-100 rounded text-sm text-gray-600 space-y-1">
              <p><strong>ID:</strong> {selectedSale.id}</p>
              <p><strong>Transaction ID:</strong> {selectedSale.transactionId}</p>
              <p><strong>Cliente:</strong> {selectedSale.customerName}</p>
              <p><strong>Referencia:</strong> {selectedSale.reference}</p>
              <p><strong>Monto:</strong> {formatters.cash(selectedSale.amount)}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Novedad *</label>
                <select
                  value={reportForm.type}
                  onChange={(e) => setReportForm(prev => ({...prev, type: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="">Selecciona un tipo</option>
                  <option value="error_monto">Error en Monto</option>
                  <option value="error_saldo">Error en Saldo</option>
                  <option value="cliente_reclamo">Reclamo del Cliente</option>
                  <option value="fallo_procesamiento">Fallo en Procesamiento</option>
                  <option value="transaccion_duplicada">Transacción Duplicada</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
                <textarea
                  value={reportForm.description}
                  onChange={(e) => setReportForm(prev => ({...prev, description: e.target.value}))}
                  placeholder="Describe el problema o novedad encontrada..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm h-24 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Evidencia (opcional)</label>
                <input
                  type="file"
                  onChange={handleEvidenceChange}
                  accept="image/*,.pdf,.doc,.docx"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
                {reportForm.evidence && (
                  <p className="text-xs text-green-600 mt-1">Archivo seleccionado correctamente</p>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={submitReport}
                disabled={reportingNovelty || !reportForm.type || !reportForm.description.trim()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-400 text-sm font-medium"
              >
                {reportingNovelty ? 'Reportando...' : 'Reportar Novedad'}
              </button>
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setSelectedSale(null);
                  setReportForm({type: '', description: '', evidence: null});
                }}
                disabled={reportingNovelty}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:bg-gray-400 text-sm font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});